import { ApolloClient } from '@apollo/client';
import * as Sentry from '@sentry/react';
import type {
  Client,
  FilestackError,
  PickerFileMetadata,
  PickerOptions,
  Security,
} from 'filestack-js';

import { gengql } from '@/__generated__';
import config from '@/config';

const CREATE_FILESTACK_SECURITY_UPLOAD = gengql(/* GraphQL */ `
  mutation generateFilestackSecurityUpload {
    generateFilestackSecurityUpload {
      policy
      signature
    }
  }
`);

const CREATE_READ_POLICY = gengql(/* GraphQL */ `
  mutation generateFilestackSecurityRead($handle: String!) {
    generateFilestackSecurityRead(handle: $handle) {
      policy
      signature
    }
  }
`);

export type FileUploadFailureReason = 'empty_file' | 'network' | 'unknown';

export class FileUploadError extends Error {
  readonly reason: FileUploadFailureReason;
  readonly filename?: string;

  // Kept off the Sentry payload today; CV filenames often carry a person's name
  // (ENG-5599), so do not add it to captureException extras or tags.
  constructor(file: PickerFileMetadata | undefined, cause: unknown) {
    super(`File upload failed: ${describeCause(cause)}`);
    this.name = 'FileUploadError';
    this.filename = file?.filename;
    this.reason = classifyFailure(cause);
  }
}

// The upload error the Filestack client emits as 'upload.error'. The picker
// itself hands onFileUploadFailed the failed file record, which carries no cause.
type UploadError = FilestackError & { details?: { code?: number } };

function isUploadError(cause: unknown): cause is UploadError {
  return cause instanceof Error && 'type' in cause && 'details' in cause;
}

// Its message can name the file (ENG-5599), so only the type and HTTP status are kept.
function describeCause(cause: unknown): string {
  if (typeof cause === 'string') return cause;
  if (isUploadError(cause)) {
    return [cause.type, cause.details?.code ?? 'no response'].join(' ');
  }
  if (cause instanceof Error) return cause.message;
  return 'no detail';
}

function classifyFailure(cause: unknown): FileUploadFailureReason {
  // The picker's own wording for a zero-byte file.
  if (cause === 'Size is equal to 0kb') return 'empty_file';
  if (!navigator.onLine) return 'network';
  // A request that got no HTTP response never reached Filestack.
  if (isUploadError(cause)) {
    return cause.type === 'request' && cause.details?.code === undefined ? 'network' : 'unknown';
  }
  if (cause instanceof Error && /network/i.test(cause.message)) return 'network';
  return 'unknown';
}

// Returns the message to show the user; `subject` names what was being uploaded.
// Reports to Sentry only the failures that are not the user's doing, as handled
// errors, so real Filestack or network outages stay visible.
export function handleFileUploadError(error: unknown, subject = 'file'): string {
  const reason = error instanceof FileUploadError ? error.reason : 'unknown';
  if (reason === 'empty_file') {
    const filename = (error as FileUploadError).filename;
    return `${filename ? `"${filename}"` : 'The file'} is empty. Choose a different file.`;
  }

  Sentry.captureException(error, { tags: { file_upload_failure: reason } });
  if (reason === 'network') {
    return `The ${subject} upload failed due to a connection problem. Please try again.`;
  }
  return 'The upload failed. Please try again.';
}

export async function openFileDialog(graphql: ApolloClient<any>, options: PickerOptions = {}) {
  const { accept, transformations, maxSize, fromSources, ...rest } = options;

  if (!config.filestackApiKey) {
    throw new Error('Filestack API key is not defined');
  }

  const [filestack, { data }] = await Promise.all([
    import('filestack-js'),
    graphql.mutate({ mutation: CREATE_FILESTACK_SECURITY_UPLOAD }),
  ]);

  const security = data?.generateFilestackSecurityUpload;
  const client = filestack.init(config.filestackApiKey, {
    security: {
      policy: security?.policy as string,
      signature: security?.signature as string,
    },
  });

  return pick(client, {
    accept,
    transformations,
    fromSources: fromSources || [
      'local_file_system',
      'url',
      'googledrive',
      'dropbox',
      'box',
      'onedrive',
      'onedriveforbusiness',
      'audio',
    ],
    maxSize: maxSize || 10 * 1024 * 1024, // defaults to 10 MB
    ...rest,
  });
}

// Resolves with the uploaded files, or with [] when the user closes the picker
// without uploading anything. Rejects with a FileUploadError only when nothing
// uploaded and an upload actually failed. Oversized and wrong-type files never get here: the picker
// refuses them at selection and tells the user itself.
function pick(client: Client, options: PickerOptions) {
  return new Promise<PickerFileMetadata[]>((resolve, reject) => {
    let settled = false;
    let lastFailure: FileUploadError | undefined;
    let lastUploadError: unknown;
    // With allowManualRetry the picker never reports done while a failure remains,
    // so a close after a partial success must still hand back what did upload.
    const finished: PickerFileMetadata[] = [];

    const settle = (files: PickerFileMetadata[]) => {
      if (settled) return;
      settled = true;
      if (files.length > 0 || !lastFailure) resolve(files);
      else reject(lastFailure);
    };

    client.on('upload.error', (error: unknown) => {
      lastUploadError = error;
    });

    client
      .picker({
        ...options,
        // Keeps the picker open on a failed upload so the user can retry in place.
        allowManualRetry: true,
        // Filestack retries 10 times by default, which leaves a dropped upload stuck
        // on "Uploading" for about 100 s before the failure and retry button show.
        uploadConfig: { retry: 3, ...options.uploadConfig },
        onFileUploadFailed: (file, error) => {
          // A zero-byte file never uploads, so the picker's own string must win over
          // an error left by an earlier file.
          const cause = typeof error === 'string' ? error : (lastUploadError ?? error);
          lastFailure = new FileUploadError(file, cause);
          lastUploadError = undefined;
          options.onFileUploadFailed?.(file, error);
        },
        onFileUploadFinished: (file) => {
          finished.push(file);
          options.onFileUploadFinished?.(file);
        },
        onUploadDone: (result) => {
          settle(result.filesUploaded ?? []);
          options.onUploadDone?.(result);
        },
        onClose: () => {
          settle(finished);
          options.onClose?.();
        },
      })
      .open()
      .catch((error: unknown) => {
        lastFailure = new FileUploadError(undefined, error);
        settle([]);
      });
  });
}

export async function presignAttachmentURL(graphql: ApolloClient<any>, filestackURL: string) {
  if (!filestackURL || !filestackURL.includes('filestackcontent.com')) {
    return filestackURL;
  }

  if (!config.filestackApiKey) {
    throw new Error('Filestack API key is not defined');
  }

  const url = new URL(filestackURL);
  const handle = url.pathname.split('/').pop();
  if (!handle) {
    throw new Error('Invalid filestack URL');
  }

  const { data } = await graphql.mutate({ mutation: CREATE_READ_POLICY, variables: { handle } });
  const { policy, signature } = data?.generateFilestackSecurityRead as Security;

  return `${url.origin}${url.pathname}?policy=${policy}&signature=${signature}`;
}

export function openImageDialog(graphql: ApolloClient<any>, { dimensions, accept }: any = {}) {
  const { width, height } = dimensions || {};

  return openFileDialog(graphql, {
    accept: accept || 'image/*',
    fromSources: [
      'local_file_system',
      'url',
      'imagesearch',
      'facebook',
      'instagram',
      'googledrive',
      'dropbox',
      'box',
      'googlephotos',
      'onedrive',
      'onedriveforbusiness',
      'webcam',
    ],
    transformations: dimensions
      ? {
          crop: {
            aspectRatio: width / height,
            force: true,
          },
        }
      : undefined,
  });
}

export function openPictureDialog(graphql: ApolloClient<any>) {
  return openImageDialog(graphql, { dimensions: { width: 220, height: 220 } });
}

export function openResumeDialog(graphql: ApolloClient<any>) {
  return openFileDialog(graphql, {
    accept: ['.doc', '.docx', '.pdf', '.rtf'],
    maxSize: 3 * 1024 * 1024, // 3 MB
    fromSources: [
      'local_file_system',
      'url',
      'googledrive',
      'dropbox',
      'box',
      'onedrive',
      'onedriveforbusiness',
    ],
  });
}

export function openSvgDialog(graphql: ApolloClient<any>) {
  return openFileDialog(graphql, {
    accept: ['image/svg+xml'],
    fromSources: [
      'local_file_system',
      'url',
      'googledrive',
      'dropbox',
      'box',
      'onedrive',
      'onedriveforbusiness',
    ],
    transformations: {
      crop: false,
      circle: false,
      rotate: false,
    },
  });
}
